<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeHide from "@dsk/icons/LeHide.vue";
import { computed } from "vue";
import type { Composer } from "vue-i18n";

import { DictionariesHelper, MatomoHelper } from "@/helpers";
import { type Dictionary, MatomoEventAction, MatomoEventCategory } from "@/types";

const props = defineProps<{
  i18n: Composer;
  selectedDictionary: Dictionary;
  switchedFromDictionary: Dictionary;
}>();

const emit = defineEmits<{
  closeToast: [];
  revertClick: [toDictionary: Dictionary];
  showSettingClick: [];
}>();

const fromTitle = computed<string>(() => props.switchedFromDictionary.title);

const toTitle = computed<string>(() => props.selectedDictionary.title);

const onRevertClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.RevertDictionaryJump,
    "Clicked button to return to previous dictionary"
  );

  if (DictionariesHelper.isCompanyDictionary(props.switchedFromDictionary)) {
    MatomoHelper.sendEvent(
      MatomoEventCategory.Search,
      MatomoEventAction.RevertDictionaryJump,
      "Clicked button to return to previous company dictionary"
    );
  }

  // If language pairs are changed, e.g. daen to dade. This is not related to the
  // language selector
  if (
    props.selectedDictionary.tags.find((tag) => tag.startsWith("group-")) !==
    props.switchedFromDictionary.tags.find((tag) => tag.startsWith("group-"))
  ) {
    MatomoHelper.sendEvent(
      MatomoEventCategory.Search,
      MatomoEventAction.RevertDictionaryJump,
      "Language pairing changed"
    );
  }

  emit("revertClick", props.switchedFromDictionary);
  emit("closeToast");
};

const onShowSettingClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickDictionaryJumpToastSetting,
    "Clicked button to change dictionary jump toast setting"
  );

  emit("showSettingClick");
};
</script>

<template>
  <div class="le-search-switched-dictionary-toast">
    <i18n-t :i18n="props.i18n" tag="span" keypath="le-search-switched-dictionary-toast.body">
      <template #fromDictionary>
        <span class="le-search-switched-dictionary-toast__dictionary-data">
          <span class="le-search-switched-dictionary-toast__dictionary-title">
            {{ fromTitle }}
          </span>
          <!-- Intentional break to avoid whitespace after publisher -->
          <span class="le-search-switched-dictionary-toast__dictionary-publisher">
            ({{ props.switchedFromDictionary.publisher }})</span
          >
        </span>
      </template>
      <template #toDictionary>
        <span class="le-search-switched-dictionary-toast__dictionary-data">
          <span class="le-search-switched-dictionary-toast__dictionary-title">
            {{ toTitle }}
          </span>
          <!-- Intentional break to avoid whitespace after publisher -->
          <span class="le-search-switched-dictionary-toast__dictionary-publisher">
            ({{ props.selectedDictionary.publisher }})</span
          >
        </span>
      </template>
    </i18n-t>
    <button type="button" class="le-search-switched-dictionary-toast__revert-button" @click="onRevertClick()">
      {{ props.i18n.t("le-search-switched-dictionary-toast.revert") }}
    </button>
    <LeIconButton
      :aria-label="props.i18n.t('le-search-switched-dictionary-toast.setting')"
      color="accent"
      @click="onShowSettingClick()"
    >
      <LeHide role="presentation" />
    </LeIconButton>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-search-switched-dictionary-toast {
  display: grid;
  grid-template-columns: 1fr 90px 28px;
  gap: 6px;
  align-items: center;

  &__revert-button {
    border: none;
    background: none;
    text-decoration: underline;
    white-space: nowrap;

    &.focus-visible {
      border-radius: 4px;
      .focus-outline(yellow);
    }
  }

  ::v-deep(&__dictionary-data) {
    padding: 0 4px;
    border-radius: 2px;
    background: @colors[@accent][400];
    color: @colors[@neutrals][500];
    word-break: break-word;
    hyphens: auto;
  }

  ::v-deep(&__dictionary-title) {
    font-weight: 600;
  }

  ::v-deep(&__dictionary-publisher) {
    font-style: italic;
  }
}
</style>
